class AnnouncementMarquee extends HTMLElement {
  constructor() {
    super();
    this.wrapper = null;
    this.templateWrapper = null;
    this.firstTrack = null;
    this.secondTrack = null;
    this.isConnectedOnce = false;
    this.resizeHandler = this.debounce(this.handleResize.bind(this), 150);
  }

  connectedCallback() {
    if (this.isConnectedOnce) return; // Prevent double-init in editors/iframes
    this.isConnectedOnce = true;

    this.wrapper = this.querySelector('.announcement-bar__scroller');
    const initialContent = this.querySelector('.announcement-bar__text-wrapper');

    if (!this.wrapper || !initialContent) return;

    // Preserve a clean template from original markup
    this.templateWrapper = initialContent.cloneNode(true);

    // Build tracks fresh from template
    this.buildTracks();
    this.updateSpeed();

    window.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  handleResize() {
    // Rebuild from scratch to avoid incremental DOM growth
    this.buildTracks();
    this.updateSpeed();
    this.restartAnimation();
  }

  buildTracks() {
    if (!this.wrapper || !this.templateWrapper) return;

    // Clear scroller to avoid accumulation
    this.wrapper.innerHTML = '';

    // Build the first track to at least cover the container width
    this.firstTrack = this.createFilledTrack();
    this.wrapper.appendChild(this.firstTrack);

    // Duplicate track once for seamless loop
    this.secondTrack = this.firstTrack.cloneNode(true);
    this.secondTrack.setAttribute('aria-hidden', 'true');
    this.wrapper.appendChild(this.secondTrack);
  }

  createFilledTrack() {
    const track = document.createElement('div');
    track.className = 'announcement-bar__text-wrapper';

    // Add at least one set
    const maxRepeats = 50; // hard cap to prevent runaway DOM
    let repeats = 0;
    const containerWidth = Math.max(0, this.offsetWidth);
    // track is detached from the document until buildTracks() appends it,
    // so track.offsetWidth would always read 0 here — measure one unit
    // via estimateTemplateWidth() instead and accumulate arithmetically.
    const unitWidth = this.estimateTemplateWidth();

    // Helper to append one template set (text + separator)
    const appendOneSet = () => {
      const nodes = this.templateWrapper.cloneNode(true).childNodes;
      for (const node of nodes) {
        track.appendChild(node.cloneNode(true));
      }
    };

    // Append until we exceed container width by one extra set
    appendOneSet();
    repeats += 1;
    let accumulatedWidth = unitWidth;

    // Measure-driven fill, with a hard ceiling
    while (repeats < maxRepeats && accumulatedWidth < containerWidth + unitWidth) {
      appendOneSet();
      accumulatedWidth += unitWidth;
      repeats += 1;
    }

    return track;
  }

  estimateTemplateWidth() {
    // Estimate width of a single template set by measuring a hidden clone
    const probe = this.templateWrapper.cloneNode(true);
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'nowrap';
    document.body.appendChild(probe);
    const width = probe.offsetWidth || 0;
    document.body.removeChild(probe);
    return Math.max(1, width);
  }

  updateSpeed() {
    // Duration is proportional to width of one track; CSS anim moves -50% which equals one track
    const speedSetting = this.getAttribute('data-speed') || 'normal';

    const pixelsPerSecondMap = {
      slow: 40,
      normal: 80,
      fast: 120,
    };

    const pixelsPerSecond = pixelsPerSecondMap[speedSetting] || pixelsPerSecondMap.normal;
    const trackWidth = this.firstTrack ? this.firstTrack.offsetWidth : this.offsetWidth;
    const durationSeconds = Math.max(5, Math.round((trackWidth / Math.max(1, pixelsPerSecond))));

    this.style.setProperty('--marquee-speed', `${durationSeconds}s`);
  }

  restartAnimation() {
    // Toggle animation to ensure updated duration takes effect
    const scroller = this.wrapper;
    if (!scroller) return;
    const prev = scroller.style.animation;
    scroller.style.animation = 'none';
    // Force reflow
    // eslint-disable-next-line no-unused-expressions
    scroller.offsetHeight;
    scroller.style.animation = prev || '';
  }

  debounce(func, wait) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), wait);
    };
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}